'use client'

import { brandingConfig, getCSSVariables } from '@/config/branding'
import { type ReactNode, useEffect } from 'react'

interface BrandingProviderProps {
	children: ReactNode
}

/**
 * BrandingProvider
 *
 * Applies the complete design system to the document at runtime.
 * Reading from platform.config.json (via brandingConfig), it sets:
 *
 * 1. --brand-*      → hex values for bg-brand-*, text-brand-* Tailwind utilities
 * 2. shadcn HSL vars → drives ALL shadcn/ui primitives (Button, Input, Card, etc.)
 *
 * This is the bridge that makes platform.config.json the single source of
 * truth for the entire visual system across any project.
 */
export function BrandingProvider({ children }: BrandingProviderProps) {
	useEffect(() => {
		const root = document.documentElement
		const cssVars = getCSSVariables()

		// Apply all design tokens (brand + shadcn) in one pass
		Object.entries(cssVars).forEach(([key, value]) => {
			root.style.setProperty(key, value)
		})

		// Update document metadata
		document.title = brandingConfig.appName

		const metaDescription = document.querySelector('meta[name="description"]')
		if (metaDescription) {
			metaDescription.setAttribute('content', brandingConfig.description)
		}

		const metaThemeColor = document.querySelector('meta[name="theme-color"]')
		if (metaThemeColor) {
			metaThemeColor.setAttribute('content', brandingConfig.colors.primary)
		}
	}, [])

	return <>{children}</>
}
